import {
  getUsers,
  addPermissions,
  removePermissions,
  addRoles,
  removeRoles,
  addTeams,
  removeTeams,
  activeUser,
  deleteUser,
} from '@/api/user';
import { Observer } from '@/utils/observer';
import { allPermission, generatePermissionComment } from '@/utils/permission';
import { companyAllActiveRoles } from '@/utils/role';
import { companyActiveTeam } from '@/utils/team';


type IUserInfo = IUser.IResponse.IUserDetailInfo;
type IGetUser = IUser.IRequest.IGetUser;

interface IUserManage {
  data: IUserInfo[];
  defaultParams: IGetUser;
  editData: {
    userInfo: IUserInfo | null;
    isCreate: boolean;
  };
  isMore: boolean;
  requestGetUsers(searchKey?: string): Promise<string>;
  requestGetMoreUsers(): Promise<string>;
  requestUpdateActive(params: IUser.IRequest.IActiveUser): Promise<string>;
  requestAddPermissions(params: string[]): Promise<string>;
  requestDeletePermissions(params: string[]): Promise<string>;
  requestAddRoles(params: string[]): Promise<string>;
  requestDeleteRoles(params: string[]): Promise<string>;
  requestAddTeams(params: string[]): Promise<string>;
  requestDeleteTeams(params: string[]): Promise<string>;
  requestDeleteUser(userId: string): Promise<string>;
  editUser(userInfo: IUserInfo): void;
  addUserPermissions(permissionIds: string[]): void;
  removeUserPermissions(permissionIds: string[]): void;
  addUserRoles(roleIds: string[]): void;
  removeUserRoles(roleIds: string[]): void;
  addUserTeams(teamIds: string[]): void;
  removeUserTeams(teamIds: string[]): void;
  updateIsMore(length: number): void;
  updateActive(params: IUser.IRequest.IActiveUser): void;
  deleteUser(userId: string): void;
}

const mapUsers = (users: IUserInfo[]): IUserInfo[] => {
  return users.map((user) => {
    user.permissions = generatePermissionComment(user.permissions);
    return user;
  });
};

export const userObserver = new Observer();

export const userManage: IUserManage = {
  data: [],
  editData: {
    userInfo: null,
    isCreate: false,
  },
  defaultParams: {
    pagesize: 20,
    pageindex: 1,
    retrueRolePermission: true,
    retrueTeam: true,
  },
  isMore: false,
  requestGetUsers(userId) {
    this.defaultParams = {
      pagesize: 20,
      pageindex: 1,
      retrueRolePermission: true,
      retrueTeam: true,
    };
    return new Promise((resolve, reject) => {
      if (userId) {
        this.defaultParams.id = userId;
      }
      getUsers(this.defaultParams).then((res) => {
        this.data = mapUsers(res.data);
        this.updateIsMore(res.data.length);
        resolve(res.message);
      }).catch((err) => {
        reject(err);
      });
    });
  },
  requestGetMoreUsers() {
    return new Promise((resolve, reject) => {
      const pageIndex = this.defaultParams.pageindex || 1;
      this.defaultParams.pageindex = pageIndex + 1;
      getUsers(this.defaultParams).then((res) => {
        const users = mapUsers(res.data);
        this.data = this.data.concat(users);
        this.updateIsMore(res.data.length);
        resolve(res.message);
      }).catch((err) => {
        reject(err);
      });
    });
  },
  requestUpdateActive(params) {
    return new Promise((resolve, reject) => {
      activeUser(params).then((res) => {
        resolve(res.message);
        this.updateActive(params);
      }).catch((err) => {
        reject(err);
      });
    });
  },
  requestAddPermissions(permissionIds) {
    return new Promise((resolve, reject) => {
      addPermissions({
        userId: this.editData.userInfo!.id,
        permissionIds,
      }).then((res) => {
        this.addUserPermissions(permissionIds);
        resolve(res.message);
      }).catch((err) => {
        reject(err.message);
      });
    });
  },
  requestDeletePermissions(permissionIds) {
    return new Promise((resolve, reject) => {
      removePermissions({
        userId: this.editData.userInfo!.id,
        permissionIds,
      }).then((res) => {
        this.removeUserPermissions(permissionIds);
        resolve(res.message);
      }).catch((err) => {
        reject(err.message);
      });
    });
  },
  requestAddRoles(roleIds) {
    return new Promise((resolve, reject) => {
      addRoles({
        userId: this.editData.userInfo!.id,
        roleIds,
      }).then((res) => {
        this.addUserRoles(roleIds);
        resolve(res.message);
      }).catch((err) => {
        reject(err.message);
      });
    });
  },
  requestDeleteRoles(roleIds) {
    return new Promise((resolve, reject) => {
      removeRoles({
        userId: this.editData.userInfo!.id,
        roleIds,
      }).then((res) => {
        this.removeUserRoles(roleIds);
        resolve(res.message);
      }).catch((err) => {
        reject(err.message);
      });
    });
  },
  requestDeleteUser(userId: string) {
    return new Promise((resolve, reject) => {
      deleteUser(userId).then((res) => {
        this.deleteUser(userId);
        resolve(res.message);
      }).catch((err) => {
        reject(err.message);
      });
    });
  },
  requestDeleteTeams(teamIds) {
    return new Promise((resolve, reject) => {
      removeTeams({
        userId: this.editData.userInfo!.id,
        teamIds,
      }).then((res) => {
        this.removeUserTeams(teamIds);
        resolve(res.message);
      }).catch((err) => {
        reject(err.message);
      });
    });
  },
  requestAddTeams(teamIds) {
    return new Promise((resolve, reject) => {
      addTeams({
        userId: this.editData.userInfo!.id,
        teamIds,
      }).then((res) => {
        this.addUserTeams(teamIds);
        resolve(res.message);
      }).catch((err) => {
        reject(err.message);
      });
    });
  },
  editUser(userInfo) {
    this.editData = {
      userInfo: {...userInfo},
      isCreate: false,
    };
  },
  addUserPermissions(permissionIds) {
    const userId = this.editData.userInfo!.id;
    const newPermissions = allPermission.filter((permission) => permissionIds.includes(permission.id));
    for (const user of this.data) {
      if (user.id === userId) {
        user.permissions = user.permissions.concat(newPermissions);
        break;
      }
    }
    userObserver.emit('UPDATE_USER_LIST');
  },
  removeUserPermissions(permissionIds) {
    const userId = this.editData.userInfo!.id;
    const newPermissions = this.editData.userInfo!.permissions.filter((permission) => {
      return !permissionIds.includes(permission.id);
    });
    for (const user of this.data) {
      if (user.id === userId) {
        user.permissions = newPermissions;
        break;
      }
    }
    userObserver.emit('UPDATE_USER_LIST');
  },
  addUserRoles(roleIds) {
    const userId = this.editData.userInfo!.id;
    const newRoles = companyAllActiveRoles.filter((role) => roleIds.includes(role.id));
    for (const user of this.data) {
      if (user.id === userId) {
        user.roles = user.roles.concat(newRoles);
        break;
      }
    }
    userObserver.emit('UPDATE_USER_LIST');
  },
  removeUserRoles(roleIds) {
    const userId = this.editData.userInfo!.id;
    const newRoles = this.editData.userInfo!.roles.filter((role) => {
      return !roleIds.includes(role.id);
    });
    for (const user of this.data) {
      if (user.id === userId) {
        user.roles = newRoles;
        break;
      }
    }
    userObserver.emit('UPDATE_USER_LIST');
  },
  addUserTeams(teamIds) {
    const userId = this.editData.userInfo!.id;
    const newTeams = companyActiveTeam.filter((team) => teamIds.includes(team.id));
    for (const user of this.data) {
      if (user.id === userId) {
        user.teams = user.teams.concat(newTeams);
        break;
      }
    }
    userObserver.emit('UPDATE_USER_LIST');
  },
  removeUserTeams(teamIds) {
    const userId = this.editData.userInfo!.id;
    const newTeams = this.editData.userInfo!.teams.filter((team) => {
      return !teamIds.includes(team.id);
    });
    for (const user of this.data) {
      if (user.id === userId) {
        user.teams = newTeams;
        break;
      }
    }
    userObserver.emit('UPDATE_USER_LIST');
  },
  updateIsMore(length) {
    if (length < this.defaultParams.pagesize!) {
      this.isMore = false;
    } else {
      this.isMore = true;
    }
  },
  updateActive(user) {
    for (const item of this.data) {
      if (item.id === user.userId) {
        item.active = user.active;
        break;
      }
    }
    userObserver.emit('UPDATE_USER_LIST');
  },
  deleteUser(userId) {
    const index = this.data.findIndex((team) => team.id === userId);
    this.data.splice(index, 1);
    userObserver.emit('UPDATE_USER_LIST');
  },
};
